/**
 * Account side of chart removal.
 *
 * A removed (tombstoned) Patient must not keep a live practice Account. Every Account-driven
 * surface (patients list, dashboard counts, practice patient Group) reads Account status, so an
 * active Account makes the tombstone look like a live patient (ENG-5780).
 *
 * Free functions rather than `MemberAccount` methods: `MemberAccount.ts` is near its size cap.
 * `Member.remove` / `Member.restore` call these right after the Patient write.
 */
import type { MedplumClient } from "@medplum/core";
import { createLogger } from "@/lib/logger";
import { MemberAccount } from "./MemberAccount";

const logger = createLogger("account-removal");

// a member has a handful of Accounts; paging guards the rare long tail
const PAGE_SIZE = 100;

export interface AccountWriteFailure {
  accountId: string;
  practiceId: string | null;
  error: unknown;
}

export interface AccountRemovalResult {
  /** Accounts this call wrote. */
  changed: MemberAccount[];
  /** Accounts whose write threw. The other Accounts were still written. */
  failed: AccountWriteFailure[];
}

/**
 * Closes a removed member's Accounts in all practices. Removal is a Patient-level state, so no
 * practice may keep the chart live.
 *
 * - An active Account goes inactive with the `removal` marker. `Member.restore` reopens it.
 * - An inactive Account with the `hint` marker gets `removed-while-inactive`.
 * - An inactive Account with no marker gets `removed-while-unmarked`.
 * - Both stay inactive, and syncs leave them alone. The marker records the prior state, so
 *   `Member.restore` can put it back.
 * - A provider-closed or removal-marked Account is not written.
 *
 * Best-effort per Account: a failed write is logged and returned in `failed`, and the loop
 * continues. Idempotent, so a re-run converges. Writes once per changed Account.
 */
export async function closeAccountsForRemovedMember(
  medplum: MedplumClient,
  memberId: string,
): Promise<AccountRemovalResult> {
  const accounts = await searchAllAccountsForMember(medplum, memberId);
  return writeEachAccount({
    memberId,
    accounts,
    operation: "close",
    write: async (account) => {
      if (account.isActive) {
        return account.markInactive(medplum, { source: MemberAccount.DeactivationSource.Removal });
      }
      if (!account.isInactive || account.isSyncProtected) return undefined;
      const source = account.deactivationSource
        ? MemberAccount.DeactivationSource.RemovedWhileInactive
        : MemberAccount.DeactivationSource.RemovedWhileUnmarked;
      return account.markInactive(medplum, { source });
    },
  });
}

/**
 * Undoes {@link closeAccountsForRemovedMember} on restore. Best-effort per Account, with the same
 * failure contract as the close. Writes once per changed Account.
 *
 * - A `removal` Account was active at removal, so it goes active again.
 * - A `removed-while-inactive` Account stays inactive and gets the `hint` marker back. The Hint
 *   membership sync owns it again.
 * - A `removed-while-unmarked` Account stays inactive and loses its marker. The Hint sync logs an
 *   unmarked Account and does not reactivate it, and migration 110 can still classify it.
 * - Provider-closed Accounts are not written.
 */
export async function reopenAccountsClosedByRemoval(
  medplum: MedplumClient,
  memberId: string,
): Promise<AccountRemovalResult> {
  const accounts = await searchAllAccountsForMember(medplum, memberId);
  return writeEachAccount({
    memberId,
    accounts,
    operation: "reopen",
    write: async (account) => {
      if (!account.isInactive) return undefined;
      if (account.deactivationSource === MemberAccount.DeactivationSource.Removal) {
        return account.markActive(medplum);
      }
      if (account.deactivationSource === MemberAccount.DeactivationSource.RemovedWhileInactive) {
        return account.markInactive(medplum, { source: MemberAccount.DeactivationSource.Hint });
      }
      if (account.deactivationSource === MemberAccount.DeactivationSource.RemovedWhileUnmarked) {
        return clearDeactivationSource(medplum, account);
      }
      return undefined;
    },
  });
}

/** Removes the marker and keeps the Account inactive. */
async function clearDeactivationSource(
  medplum: MedplumClient,
  account: MemberAccount,
): Promise<MemberAccount> {
  const { memberRef, ownerPracticeRef } = account;
  if (!memberRef || !ownerPracticeRef) {
    throw new Error("clearDeactivationSource: member or practice not found");
  }
  return MemberAccount.update(medplum, account.id, {
    member: memberRef,
    practice: ownerPracticeRef,
    deactivationSource: null,
  });
}

/** Reads every page before any write, so a write cannot shift the offset of a later page. */
async function searchAllAccountsForMember(
  medplum: MedplumClient,
  memberId: string,
): Promise<MemberAccount[]> {
  const accounts: MemberAccount[] = [];
  for (let offset = 0; ; offset += PAGE_SIZE) {
    const page = await MemberAccount.search(
      medplum,
      { member: { reference: `Patient/${memberId}` } },
      { limit: PAGE_SIZE, offset, sort: "_lastUpdated" },
    );
    accounts.push(...page);
    if (page.length < PAGE_SIZE) return accounts;
  }
}

interface WriteEachAccountParams {
  memberId: string;
  accounts: MemberAccount[];
  operation: "close" | "reopen";
  write: (account: MemberAccount) => Promise<MemberAccount | undefined>;
}

/** Runs `write` on each Account in sequence. A throw is logged and recorded, never rethrown. */
async function writeEachAccount({
  memberId,
  accounts,
  operation,
  write,
}: WriteEachAccountParams): Promise<AccountRemovalResult> {
  const result: AccountRemovalResult = { changed: [], failed: [] };
  for (const account of accounts) {
    try {
      const updated = await write(account);
      if (updated) result.changed.push(updated);
    } catch (error) {
      const failure = {
        accountId: account.id,
        practiceId: account.ownerPracticeId ?? null,
        error,
      };
      result.failed.push(failure);
      logger.error(`Failed to ${operation} an account for a removed member`, {
        memberId,
        ...failure,
      });
    }
  }
  return result;
}
